import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  // Create api instance with interceptor to always use latest token
  const api = axios.create({
    baseURL: API
  });

  // Add request interceptor to always include the latest token
  api.interceptors.request.use(
    (config) => {
      const currentToken = localStorage.getItem('token');
      if (currentToken) {
        config.headers.Authorization = `Bearer ${currentToken}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Add response interceptor to handle 401/403 errors globally
  api.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response && (error.response.status === 401 || error.response.status === 403)) {
        // Only logout if we have a token (to avoid logout loops)
        const currentToken = localStorage.getItem('token');
        if (currentToken && error.config.url === '/auth/me') {
          // Token is invalid, clear it
          localStorage.removeItem('token');
          setToken(null);
          setUser(null);
        }
      }
      return Promise.reject(error);
    }
  );

  useEffect(() => {
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password) => {
    const response = await api.post('/auth/login', { username, password });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
    return response.data;
  };

  const register = async (email, username, password) => {
    const response = await api.post('/auth/register', { email, username, password });
    return response.data;
  };

  const verifyEmail = async (email, code) => {
    const response = await api.post('/auth/verify-email', { email, code });
    const { token: newToken } = response.data;
    localStorage.setItem('token', newToken);
    setToken(newToken);
    await fetchUser();
    return response.data;
  };

  const resendVerification = async (email) => {
    const response = await api.post('/auth/resend-verification', { email });
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const updateBalance = (newBalance) => {
    if (user) {
      setUser({ ...user, site_balance: newBalance });
    }
  };

  const linkRoblox = async (cookie) => {
    const response = await api.post('/roblox/link', { cookie });
    await fetchUser();
    return response.data;
  };

  const unlinkRoblox = async () => {
    const response = await api.post('/roblox/unlink');
    await fetchUser();
    return response.data;
  };

  const refreshRobloxBalance = async () => {
    try {
      await api.post('/roblox/refresh-balance');
      await fetchUser();
    } catch (error) {
      console.error('Failed to refresh Roblox balance:', error);
    }
  };

  const refreshRobloxAvatar = async () => {
    try {
      await api.post('/roblox/refresh-avatar');
      await fetchUser();
    } catch (error) {
      console.error('Failed to refresh Roblox avatar:', error);
    }
  };

  const refreshRobloxProfile = async () => {
    try {
      // Refresh both avatar and balance
      await Promise.all([
        api.post('/roblox/refresh-avatar').catch(() => {}),
        api.post('/roblox/refresh-balance').catch(() => {})
      ]);
      await fetchUser();
    } catch (error) {
      console.error('Failed to refresh Roblox profile:', error);
    }
  };

  const value = {
    user,
    loading,
    token,
    api,
    login,
    register,
    verifyEmail,
    resendVerification,
    logout,
    updateBalance,
    linkRoblox,
    unlinkRoblox,
    refreshUser: fetchUser,
    refreshRobloxBalance,
    refreshRobloxAvatar,
    refreshRobloxProfile
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
